// The ORL rig as CHARACTER assets.
//
// A baked ORL rig is identity-specific — it is that character's DNA, neutral
// mesh, skin weights and blendshape deltas — so it belongs in the character's
// own /ogs collection, not in public/. It ships as ONE
// packed file (src/lib/orl/orlPack.js) carrying every baked member, under an
// `orl_` prefix so it can't collide with the OGS bundle's own names.
//
// It is an OPTIONAL bundle member. The exact rig is OPT-IN for now — `?rig=orl`
// selects it, and the bundle's distilled rig2mesh is what drives a character by
// default; a bundle with neither renders its head at the neutral pose (reported
// to the UI). Presence of the pack is still what makes the choice POSSIBLE, and
// it is the automatic answer for a bundle carrying no loadable rig2mesh. No
// identity to name, nothing to map — the resolution lives in
// src/ogs/inference/rigMode.ts, the only place `?rig=` is read.
//
// (src/lib/orl/ still holds riglogic.wasm, which is identity-INDEPENDENT code.
// No baked rig is committed there — public/orl/<slug>/ is bake staging only.)

/** Members inside the pack, in the deformer's expected key order. This is the
 *  bake's output list (src/lib/orl/bakeFromDna.js) — the packer writes exactly
 *  these, in this order, which is part of the container's byte-identity
 *  contract. `mesh_idx.bin` rides along for completeness; nothing at runtime
 *  reads it (the OGS branch owns its own topology). */
export const ORL_BUNDLE_FILES = [
  "manifest.json",
  "bs_targets.json",
  "head_behavior.dna",
  "neutral_pos.bin",
  "joint_neutral.bin",
  "joint_parents.bin",
  "inverse_bind.bin",
  "skin_idx.bin",
  "skin_w.bin",
  "bs_index.bin",
  "bs_delta.bin",
  "mesh_idx.bin",
];

/** Members the deformer + rig actually need — what a pack must carry to be
 *  usable. Everything else in ORL_BUNDLE_FILES is carried but never read. */
export const ORL_REQUIRED_MEMBERS = ORL_BUNDLE_FILES.filter((f) => f !== "mesh_idx.bin");

/** The packed bundle's filename, in a static dir and inside a /ogs listing. */
export const ORL_PACK_FILE = "pack.bin";

/** Bundle-side asset name: pack.bin -> orl_pack.bin */
export const orlAssetName = (file) => `orl_${file}`;

/** Every ORL asset name, for the loader's optional-file list. */
export const orlBundleAssetNames = () => [orlAssetName(ORL_PACK_FILE)];

/** True when a listing/VFS carries the ORL pack. One file now, so a partial
 *  upload is no longer expressible — the pack either parses or it throws. */
export function hasOrlBundle(hasFile) {
  return !!hasFile(orlAssetName(ORL_PACK_FILE));
}
